import srsGraphqlRequest from "../srsGraphqlRequest";
import { GQL_GET_REPORTING_ACTIVITIES } from "./reportingActivitiesQuery";

/**
 * Fetches reporting activities for a given student within a specified date range.
 *
 * @param {string} studentId - The ID of the student.
 * @param {DateRangeFilter} dateRange - filter activities by date range and  The date range filter for the activities.
 * @param {boolean} equatedScores - filter to include equated scores in the response.
 * @param {Locale} locale - filter activities by locale.
 * @returns {Promise<GetReportingActivitiesResponse | undefined>} - A promise that resolves to the reporting activities or undefined if an error occurs.
 */
export async function fetchReportingActivities(
  studentId: string,
  dateRange: DateRangeFilter,
  equatedScores: boolean,
  locale: Locale,
): Promise<GetReportingActivitiesResponse | undefined> {
  try {
    const response = await srsGraphqlRequest<GetReportingActivitiesResponse>(
      GQL_GET_REPORTING_ACTIVITIES,
      {
        studentId,
        dateRange,
        equatedScores,
        locale
      },
    );
    return response;
  }
  catch (error) {
    console.error("Error fetching reporting activities", error);
    return undefined;
  }
}

export type GetReportingActivitiesResponse = {
  getReportingActivities: ReportingActivity[];
};

export type Locale = "EN_US" | "ES_MX";

export type DateRangeFilter = {
  dateStart: string; // ISO date string
  dateEnd: string; // ISO date string
  dateField?: "createdAt" | "updatedAt"; // Optional, defaults to 'createdAt'
};

export type ReportingActivity = {
  activityId: string;
  studentId: string;
  parentId: string;
  children: Array<ReportingActivityChild>;
  createdAt: string;
  updatedAt: string;
  timeRead?: number;
  score?: number;
  errors?: any;
  flagging?: {
    triggered: boolean;
  };
  type: string;
  scoreEstimates?: {
    ARM_estimate?: number;
  };
  scores?: {
    areaScore?: number;
    esriScore?: number;
    phonAwareScore?: number;
    vocabSize?: number;
    wcpmScore?: number;
    equatedWCPM?: number;
    decodeScore?: number;
    armScore?: number;
    // PR (Percentile Rank) fields needed for Student Summary
    ISIP_overall_universal?: number;
ISIP_overall_universal_PR?: number;
    armScorePR?: number;
    currentArmEstimate?: number;
    // Background Knowledge, Comprehension, and Vocabulary fields
    bkScore?: number;
    bkScore_PR?: number;
    compScore?: number;
    compScore_PR?: number;
    vocabularyScreenerScore?: number;
    vocabularyScreenerScore_PR?: number;
  };
  status: string;
  tags?: string[];
  displayStatus?: string;
  story?: {
    tags?: string[];
    chapters?: {
      items: Array<{
        type: string;
      }>;
    };
    grade?: string;
  };
};

export type ReportingActivityChild = {
  activityId: string;
  type: string;
  status: string;
  displayStatus: string;
  errors: any;
  scores: {
    predWcpm?: number;
    armScore?: number;
  };
};
